import { useEffect } from 'react'
import { useImports } from '@/api/endpoints/videoCutter'
import type { ToolProject } from '../model'

/**
 * Adopt editing proxies that appeared after a video was added.
 *
 * Videos imported before proxies existed carry none, but the file may have
 * been generated since — a backfill, or a re-upload of the same source.
 * Reconciling against ``GET /imports`` on open beats making the user remove
 * and re-add the video to get smooth scrubbing back.
 */
export function useProxyReconcile(
  onChange: (updater: (project: ToolProject) => ToolProject) => void,
): void {
  const imports = useImports()

  useEffect(() => {
    const rows = imports.data
    if (!rows) return
    const byPath = new Map<string, string>()
    for (const row of rows) {
      if (row.proxy_url) byPath.set(row.temp_path, row.proxy_url)
    }
    if (byPath.size === 0) return
    onChange((p) => {
      const videos = p.videos.map((v) => {
        const found = v.proxyUrl ? null : byPath.get(v.tempPath)
        return found ? { ...v, proxyUrl: found } : v
      })
      // Reference equality: returning a fresh object every render would
      // loop through ``useProjects``'s debounced write forever.
      const changed = videos.some((v, i) => v !== p.videos[i])
      return changed ? { ...p, videos } : p
    })
  }, [imports.data, onChange])
}
